<template>
  <v-data-table
    :headers="headers"
    :items="webpages"
    :options.sync="options"
    :server-items-length="total"
    :footer-props="{
      showFirstLastPage: true,
      firstIcon: 'mdi-arrow-collapse-left',
      lastIcon: 'mdi-arrow-collapse-right',
      prevIcon: 'mdi-arrow-left',
      nextIcon: 'mdi-arrow-right',
      itemsPerPageOptions: [10, 25, 50, 100],
    }"
    :loading="loading"
    class="elevation-1"
  >
    <template v-slot:top>
      <v-toolbar
        flat
        color="white"
      >
        <v-toolbar-title>{{ $t("lbl.webpages") }}</v-toolbar-title>
        <v-divider
          class="mx-4"
          inset
          vertical
        />
        <v-spacer />
        <v-dialog
          v-model="dialog"
          max-width="600px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="auth('manageWebdatas')"
              color="primary"
              class="mb-2"
              v-bind="attrs"
              v-on="on"
              :disabled="disable"
            >{{
              $t("lbl.newWebpage")
            }}</v-btn>
          </template>
          <v-form
            ref="form"
            v-model="valid"
          >
            <v-card>
              <v-card-title>
                <span class="headline">{{ formTitle }}</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="editedItem.url"
                        :rules="rules.urlRules"
                        :label="$t('lbl.url')"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      class="py-0"
                    >
                      <v-select
                        outlined
                        clearable
                        v-model="editedItem.site"
                        :items="websites"
                        item-text="url"
                        item-value="id"
                        :label="$t('lbl.website')"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      class="py-0"
                    >
                      <v-combobox
                        clearable
                        multiple
                        small-chips
                        deletable-chips
                        outlined
                        v-model="editedItem.links"
                        :items="editedItem.links"
                        :rules="rules.linkRules"
                        :label="$t('lbl.links')"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-btn
                  color="blue darken-1"
                  text
                  @click="reset"
                >{{ $t("lbl.clear") }}</v-btn>
                <v-spacer />
                <v-btn
                  color="blue darken-1"
                  text
                  @click="dialog = false"
                >{{
                  $t("lbl.cancel")
                }}</v-btn>
                <v-btn
                  color="blue darken-1"
                  text
                  :disabled="!valid"
                  @click="save"
                >{{
                  $t("lbl.save")
                }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
        </v-dialog>
      </v-toolbar>
    </template>
    <template v-slot:[`item.site`]="{ item }">
      <span>
        {{ getItem(websites, item.site).url }}
      </span>
    </template>
    <template v-slot:[`item.links`]="{ item }">
      <span>
        <v-chip
          v-for="link in item.links"
          :key="link"
        >
          {{ link }}
        </v-chip>
      </span>
    </template>
    <template v-slot:[`item.actions`]="{ item }">
      <v-icon
        class="mr-2"
        :disabled="disable"
        @click="editItem(item)"
      > mdi-pencil </v-icon>
      <v-icon
        :disabled="disable"
        @click="deleteItem(item)"
      > mdi-delete </v-icon>
    </template>
  </v-data-table>
</template>

<script>
import { mapActions } from "vuex";
import { required } from "../common/validation";
import { url } from "../common/regex";

export default {
  data: () => ({
    dialog: false,
    disable: false,
    valid: true,
    loading: true,
    options: {},
    total: 0,
    webpages: [],
    websites: [],
    editedIndex: -1,
    editedItem: {
      site: null,
      url: "",
      links: [],
    },
    defaultItem: {
      site: null,
      url: "",
      links: [],
    },
    rules: {
      urlRules: [required],
      linkRules: [required],
    },
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1
        ? this.$t("lbl.newWebpage")
        : this.$t("lbl.editWebpage");
    },
    headers() {
      const columns = [
        { text: this.$t("lbl.website"), value: "site" },
        { text: this.$t("lbl.url"), value: "url" },
        { text: this.$t("lbl.links"), value: "links" },
      ];
      if (this.auth("manageWebdatas")) {
        columns.push({
          text: this.$t("lbl.actions"),
          value: "actions",
          align: "center",
          sortable: false,
        });
      }
      return columns;
    },
  },

  watch: {
    dialog(val) {
      // eslint-disable-next-line no-unused-expressions
      val || this.close();
    },
    options: {
      handler() {
        this.initialize();
      },
      deep: true,
    },
    // eslint-disable-next-line func-names
    "editedItem.url": function (val) {
      if (val && url.test(val)) {
        this.editedItem.url = val.match(url).at(1);
      }
    },
  },

  methods: {
    ...mapActions("webdata", [
      "findWebsites",
      "findWebpages",
      "saveWebpage",
      "deleteWebpage",
    ]),

    async initialize() {
      this.loading = true;
      const filters = this.getFilters(this.options);
      try {
        const { results } = await this.findWebsites();
        this.websites = results;
      } catch (error) {
        this.$toasted.error(this.getMessageText(error.message));
      }

      try {
        const { results, totalResults } = await this.findWebpages(filters);
        this.webpages = results;
        this.total = totalResults;
      } catch (error) {
        this.$toasted.error(this.getMessageText(error.message));
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.$refs.form.reset();
    },

    close() {
      this.reset();
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
      });
    },

    editItem(item) {
      this.editedIndex = this.webpages.indexOf(item);
      this.editedItem = { ...item };
      this.dialog = true;
    },

    deleteItem(item) {
      this.$swal({
        html: this.$t("msg.deleteText", { item: item.url }),
        icon: "warning",
        showCancelButton: true,
        focusCancel: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("lbl.delete"),
        cancelButtonText: this.$t("lbl.cancel"),
      }).then((result) => {
        if (result.value) {
          this.disable = true;
          this.deleteWebpage(item.id)
            .then(() => {
              this.initialize();
              this.$toasted.success(
                this.$t("msg.delete", { item: this.$t("lbl.webpage") }),
              );
            })
            .catch((error) => {
              this.$toasted.error(this.getMessageText(error.message));
            })
            .finally(() => {
              this.disable = false;
            });
        }
      });
    },

    save() {
      this.dialog = false;
      this.disable = true;
      const editedItem = { ...this.editedItem };
      return this.saveWebpage(editedItem)
        .then(() => {
          this.initialize();
          this.$toasted.success(
            this.$t("msg.save", { item: this.$t("lbl.webpage") }),
          );
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        })
        .finally(() => {
          this.disable = false;
        });
    },
  },
};
</script>
